// Focus trap + Escape handling for modal dialogs. Apply via useFocusTrap(ref).
import { useEffect } from "react";

export function useFocusTrap(containerRef, opts = {}) {
  const { onEscape = null, autoFocus = true, enabled = true } = opts;
  useEffect(() => {
    if (!enabled) return undefined;
    const node = containerRef.current;
    if (!node) return undefined;
    const previouslyFocused = document.activeElement;

    const focusables = () =>
      Array.from(node.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )).filter((el) => !el.hasAttribute("aria-hidden"));

    if (autoFocus) {
      const f = focusables();
      (f[0] || node).focus({ preventScroll: true });
    }

    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const f = focusables();
      if (f.length === 0) return;
      const first = f[0]; const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    // Listen at document level so Escape works regardless of focus location.
    const onDocKey = (e) => {
      if (e.key === "Escape" && onEscape) onEscape();
    };
    node.addEventListener("keydown", onKey);
    document.addEventListener("keydown", onDocKey);
    return () => {
      node.removeEventListener("keydown", onKey);
      document.removeEventListener("keydown", onDocKey);
      try { previouslyFocused?.focus?.({ preventScroll: true }); } catch { /* noop */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);
}
